/*
 * Lenz Home Carousels — auto-scroll con scroll nativo en TODOS los viewports.
 *
 * v21 (antes v20.12 solo-mobile): los carousels `.image-link-blocks--auto-scroll`
 * ("Encuentra tus marcas" y "Encuéntranos también en") ya NO usan la CSS
 * animation con transform en desktop — theme.css les da overflow-x:auto
 * en todos los tamaños y este script maneja el movimiento vía scrollLeft:
 *
 *   1. Auto-scroll continuo (~0.6 px/frame ≈ 36 px/seg) en mobile y desktop.
 *   2. Interacción del usuario PAUSA: touch/swipe (nativo), rueda/trackpad
 *      y flechas (vía evento scroll), drag con mouse (pointer events).
 *      Hover con mouse solo RALENTIZA (40%), no detiene.
 *   3. Se REANUDA ~2.5s después de la última interacción.
 *   4. Loop infinito: los items están duplicados en el DOM (script inline
 *      del theme); al pasar scrollWidth/2 restamos la mitad, invisible.
 *   5. Drag con mouse: pointerdown+move mueve scrollLeft; un drag real
 *      (>4px) suprime el click para no navegar al soltar sobre un logo.
 *      Un clic sin movimiento SÍ navega (la captura del puntero se activa
 *      recién al superar el umbral — ver onPointerMove).
 */
(function () {
  'use strict';

  // Velocidad en px por SEGUNDO, no por cuadro. Antes avanzaba 0.6 px por
  // cuadro: 36 px/s a 60 Hz pero 72 px/s en pantallas de 120 Hz (Mac,
  // iPhone Pro) y 86 px/s a 144 Hz, el doble que el resto de marquesinas.
  // Ahora el avance se calcula con el tiempo real entre cuadros.
  var SCROLL_SPEED_PX_PER_SEC = 36;
  // Tope por cuadro: tras volver de una pestaña en segundo plano el primer
  // delta puede ser de varios segundos; no queremos un salto visible.
  var MAX_FRAME_DT_SEC = 0.1;

  // Hover con mouse: ralentiza a 40% (no se detiene del todo)
  var HOVER_SPEED_FACTOR = 0.4;

  // Delay para reanudar tras soltar el touch o terminar de scrollear
  var RESUME_DELAY_MS = 2500;

  // Delay corto para considerar "scroll terminó" después del último evento
  var SCROLL_SETTLE_MS = 150;

  var instances = [];
  var FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

  /**
   * Setup auto-scroll para un carousel individual.
   * @param {HTMLElement} carousel
   * @returns {{stop: Function}}
   */
  function setupCarousel(carousel) {
    var isPaused = false;
    var speedFactor = 1;   // 1 normal, HOVER_SPEED_FACTOR con mouse encima
    var scrollFrac = 0;    // acumula sub-pixeles (Safari: scrollLeft es entero)
    var isJsTriggeredScroll = false;
    var resumeTimer = null;
    var scrollSettleTimer = null;
    var halfWidth = 0;
    var rafId = null;
    var lastTs = 0;
    var destroyed = false;

    function recalcHalfWidth() {
      // El theme renderiza los items 2x en el DOM (para el efecto CSS
      // infinite scroll con translateX(-50%)). Por lo tanto, scrollWidth
      // ya incluye la duplicación; halfWidth = punto donde "reseteamos"
      // para crear loop visualmente continuo.
      halfWidth = Math.max(0, carousel.scrollWidth / 2);
    }

    function tick(ts) {
      if (destroyed) return;
      var dt = lastTs ? Math.min((ts - lastTs) / 1000, MAX_FRAME_DT_SEC) : 0;
      lastTs = ts;

      if (!isPaused && halfWidth > 0) {
        scrollFrac += SCROLL_SPEED_PX_PER_SEC * speedFactor * dt;
        var stepPx = Math.floor(scrollFrac);
        if (stepPx > 0) {
          scrollFrac -= stepPx;
          isJsTriggeredScroll = true;
          carousel.scrollLeft += stepPx;
        }

        // Loop infinito: cuando llega al medio, restamos halfWidth.
        // Esto NO es visible porque los items duplicados están en la
        // misma posición visual (los del primer set = los del segundo set).
        if (carousel.scrollLeft >= halfWidth) {
          carousel.scrollLeft -= halfWidth;
        }

        // El flag isJsTriggeredScroll evita que el scroll listener
        // de abajo interprete nuestro scroll como interacción del usuario.
        // Lo reseteamos en el siguiente frame.
        requestAnimationFrame(function () {
          isJsTriggeredScroll = false;
        });
      }

      rafId = requestAnimationFrame(tick);
    }

    function pause() {
      isPaused = true;
      if (resumeTimer) {
        clearTimeout(resumeTimer);
        resumeTimer = null;
      }
    }

    function scheduleResume() {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        if (destroyed) return;
        isPaused = false;
        recalcHalfWidth(); // por si el DOM cambió
        resumeTimer = null;
      }, RESUME_DELAY_MS);
    }

    // ── Touch interaction ────────────────────────────────────────────
    function onTouchStart() {
      pause();
    }

    function onTouchEnd() {
      scheduleResume();
    }

    // ── Manual scroll detection ──────────────────────────────────────
    // Si el usuario hace flick + scrolling continúa después de soltar,
    // queremos esperar a que termine antes de reanudar el auto-scroll.
    function onScroll() {
      if (isJsTriggeredScroll) return; // ignore nuestro propio scroll
      pause();
      if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
      scrollSettleTimer = setTimeout(scheduleResume, SCROLL_SETTLE_MS);
    }

    carousel.addEventListener('touchstart', onTouchStart, { passive: true });
    carousel.addEventListener('touchend', onTouchEnd, { passive: true });
    carousel.addEventListener('touchcancel', onTouchEnd, { passive: true });
    carousel.addEventListener('scroll', onScroll, { passive: true });

    // ── Drag-to-scroll con mouse (touch usa el scroll nativo) ─────────
    // IMPORTANTE: la captura del puntero (setPointerCapture) y la clase
    // is-dragging se activan SOLO cuando el mouse ya se movió > DRAG_THRESHOLD.
    // Antes se activaban en pointerdown: con el puntero capturado, el
    // navegador entrega pointerup/click al carousel y no al <a> del logo,
    // así que un clic simple NUNCA navegaba a la colección de la marca.
    var DRAG_THRESHOLD = 4;
    var isDragging = false;   // botón presionado sobre el carousel
    var dragMoved = false;    // superó el umbral: es un arrastre real
    var dragPointerId = null;
    var dragStartX = 0;
    var dragStartScroll = 0;

    function onPointerDown(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      isDragging = true;
      dragMoved = false;
      dragPointerId = e.pointerId;
      dragStartX = e.clientX;
      dragStartScroll = carousel.scrollLeft;
      pause(); // que el logo no se mueva bajo el cursor mientras se hace clic
    }

    function onPointerMove(e) {
      if (!isDragging) return;
      var dx = e.clientX - dragStartX;
      if (!dragMoved) {
        if (Math.abs(dx) <= DRAG_THRESHOLD) return; // todavía es un clic
        dragMoved = true;
        carousel.classList.add('is-dragging');
        try { carousel.setPointerCapture(dragPointerId); } catch (_) {}
      }
      // el scroll listener ignora esto como "interacción" — ya pausamos
      isJsTriggeredScroll = true;
      carousel.scrollLeft = dragStartScroll - dx;
    }

    function onPointerUp() {
      if (!isDragging) return;
      isDragging = false;
      carousel.classList.remove('is-dragging');
      try {
        if (carousel.hasPointerCapture && carousel.hasPointerCapture(dragPointerId)) {
          carousel.releasePointerCapture(dragPointerId);
        }
      } catch (_) {}
      scheduleResume();
      // dragMoved se limpia en el click capture (que dispara después)
      setTimeout(function () { dragMoved = false; }, 0);
    }

    // Un drag real no debe navegar al link donde se soltó el mouse
    function onClickCapture(e) {
      if (dragMoved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    // Evitar el drag nativo de imágenes/links mientras arrastramos
    function onDragStart(e) {
      if (isDragging || dragMoved) e.preventDefault();
    }

    // ── Flechas prev/next del theme (button[is=prev|next-button]) ──────
    // El theme hace un scroll SUAVE de un item; pero tick() escribe
    // scrollLeft en cada frame, lo que cancela ese scroll suave, y como
    // isJsTriggeredScroll queda en true esos eventos ni siquiera pausaban.
    // Resultado: las flechas casi no movían el carousel. Pausamos ANTES del
    // click (pointerdown / Enter / Espacio) y reanudamos como siempre.
    var controls = carousel.id
      ? document.querySelectorAll('[aria-controls="' + carousel.id + '"]')
      : [];
    function onControlPress(e) {
      if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
      pause();
      isJsTriggeredScroll = false;
      scheduleResume();
    }
    Array.prototype.forEach.call(controls, function (btn) {
      btn.addEventListener('pointerdown', onControlPress);
      btn.addEventListener('keydown', onControlPress);
    });

    carousel.addEventListener('pointerdown', onPointerDown);
    carousel.addEventListener('pointermove', onPointerMove);
    carousel.addEventListener('pointerup', onPointerUp);
    carousel.addEventListener('pointercancel', onPointerUp);
    carousel.addEventListener('click', onClickCapture, true);
    carousel.addEventListener('dragstart', onDragStart);

    // ── Hover con mouse: ralentiza (no detiene); pausa total solo en
    //    interaccion real (drag / rueda / touch) ──────────────────────
    function onMouseEnter() { if (FINE_POINTER.matches) speedFactor = HOVER_SPEED_FACTOR; }
    function onMouseLeave() { if (FINE_POINTER.matches) speedFactor = 1; }
    carousel.addEventListener('mouseenter', onMouseEnter);
    carousel.addEventListener('mouseleave', onMouseLeave);

    // Iniciar
    recalcHalfWidth();
    rafId = requestAnimationFrame(tick);

    return {
      recalc: recalcHalfWidth,
      stop: function () {
        destroyed = true;
        if (rafId) cancelAnimationFrame(rafId);
        if (resumeTimer) clearTimeout(resumeTimer);
        if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
        carousel.removeEventListener('touchstart', onTouchStart);
        carousel.removeEventListener('touchend', onTouchEnd);
        carousel.removeEventListener('touchcancel', onTouchEnd);
        carousel.removeEventListener('scroll', onScroll);
        Array.prototype.forEach.call(controls, function (btn) {
          btn.removeEventListener('pointerdown', onControlPress);
          btn.removeEventListener('keydown', onControlPress);
        });
        carousel.removeEventListener('pointerdown', onPointerDown);
        carousel.removeEventListener('pointermove', onPointerMove);
        carousel.removeEventListener('pointerup', onPointerUp);
        carousel.removeEventListener('pointercancel', onPointerUp);
        carousel.removeEventListener('click', onClickCapture, true);
        carousel.removeEventListener('dragstart', onDragStart);
        carousel.removeEventListener('mouseenter', onMouseEnter);
        carousel.removeEventListener('mouseleave', onMouseLeave);
      }
    };
  }

  function teardownAll() {
    instances.forEach(function (i) {
      try {
        i.stop();
      } catch (_) {}
    });
    instances = [];
  }

  function setupAll() {
    teardownAll();
    var carousels = document.querySelectorAll(
      '.image-link-blocks--auto-scroll'
    );
    carousels.forEach(function (c) {
      instances.push(setupCarousel(c));
    });
  }

  // ── Init ──────────────────────────────────────────────────────────
  function init() {
    setupAll();

    // El ancho de los items cambia con el viewport → recalcular el punto
    // de loop (scrollWidth/2) al redimensionar.
    window.addEventListener('resize', function () {
      instances.forEach(function (i) {
        try { i.recalc(); } catch (_) {}
      });
    });

    // Failsafe: el theme tiene un script inline (sin defer) que añade la
    // clase `.image-link-blocks--auto-scroll` durante DOMContentLoaded
    // (después de clonar los items para el loop infinito). Como nuestro
    // script es defer, podríamos llegar antes que el inline. Si setupAll
    // no encontró carousels, usamos un MutationObserver para esperar a
    // que la clase aparezca y reintentar.
    if (instances.length === 0) {
      var observer = new MutationObserver(function () {
        var carousels = document.querySelectorAll(
          '.image-link-blocks--auto-scroll'
        );
        if (carousels.length > 0) {
          observer.disconnect();
          setupAll();
        }
      });
      observer.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
        childList: true
      });
      // Cleanup después de 10s para no quedar observando indefinidamente
      setTimeout(function () {
        observer.disconnect();
      }, 10000);
    }
  }

  /**
   * Esperamos a `window.load` (no `DOMContentLoaded`) porque el theme
   * tiene scripts inline que se ejecutan en DOMContentLoaded y agregan
   * la clase `.image-link-blocks--auto-scroll` a los carousels. Como
   * nuestro script es `defer`, podríamos correr ANTES que esos inline
   * handlers. `window.load` garantiza que todo (incluyendo handlers
   * inline registrados) terminó de ejecutarse.
   */
  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init, { once: true });
  }
})();
